import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ConstraintsService from "./ConstraintsService";
import { clearStaManual, computeStaFromStdAndBlockTime, parsePositiveMinutes } from "../../../../utils/staBlockTimeAlignment";
import { normalizeAirportCode } from "../../../../utils/airportCodeUtils";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";

dayjs.extend(utc);

/**
 * 旧版计划编辑器「批量改经停机场」核心服务。
 *
 * 职责：候选航班筛选、航路字段派生、keep_all_std 时间策略、
 *       过站 / 宵禁 / 同场飞本场警告、预览摘要、可追查备注。
 *
 * 约束（规格强制）：
 *   - 时刻策略只支持 keep_all_std：STD 不变，STA = STD + 新轮档
 *   - 过站 / 宵禁 / 同场飞本场 → 警告，不拦截确认
 *   - 缺航路 → 默认拦截确认；可勾选跳过
 *   - 冗余字段按旧版口径（flight_route_id / flight_route_name /
 *     flight_route_block_time_minutes / min_block_time_minutes /
 *     block_time_minutes），禁止照搬新版字段名
 */

const WARNING_TYPES = {
    GROUND_TIME_SHORT: "ground_time_short",
    GROUND_TIME_CONFIG_MISSING: "ground_time_config_missing",
    CURFEW: "curfew",
    SAME_AIRPORT: "same_airport",
    MISSING_ROUTE: "missing_route",
};

// ─── 候选航班筛选 ─────────────────────────────────────

/**
 * 找出触碰旧机场的航班。
 * @param {Array} flights 编辑器当前航班列表
 * @param {string} fromAirport 旧机场三字码
 * @param {string[]} sides ["departure"] / ["arrival"] / ["departure","arrival"]
 * @returns {Array<{flight, affectedSides: string[]}>}
 */
function findAffectedFlights(flights, fromAirport, sides) {
    if (!flights || !fromAirport || !sides?.length) return [];

    const sideSet = new Set(sides);
    const results = [];

    for (const flight of flights) {
        const affectedSides = [];
        if (sideSet.has("departure") && flight.departure_airport === fromAirport) {
            affectedSides.push("departure");
        }
        if (sideSet.has("arrival") && flight.arrival_airport === fromAirport) {
            affectedSides.push("arrival");
        }
        if (affectedSides.length > 0) {
            results.push({ flight, affectedSides });
        }
    }

    return results;
}

// ─── 机场替换（纯字段替换，不含航路/时间） ──────────────

function replaceAirport(flight, fromAirport, toAirport, sides) {
    const updated = { ...flight };
    const sideSet = new Set(sides);

    if (sideSet.has("departure") && updated.departure_airport === fromAirport) {
        updated.departure_airport = toAirport;
    }
    if (sideSet.has("arrival") && updated.arrival_airport === fromAirport) {
        updated.arrival_airport = toAirport;
    }

    return updated;
}

// ─── 航路冗余字段派生（旧版口径） ─────────────────────

/**
 * 按旧版口径设置航路冗余字段。
 * routeData 来自 FlightRouteResponse（best-match 返回）。
 */
function applyRouteFields(flight, routeData) {
    if (!routeData) {
        return {
            ...flight,
            flight_route_id: null,
            flight_route_name: null,
            flight_route_block_time_minutes: null,
            min_block_time_minutes: null,
            block_time_minutes: null,
        };
    }

    const normalizedBH = parsePositiveMinutes(routeData.block_time_minutes ?? routeData.min_block_time_minutes);

    return {
        ...flight,
        flight_route_id: routeData.id ?? routeData.flight_route_id ?? null,
        flight_route_name: routeData.route_name ?? null,
        flight_route_block_time_minutes: normalizedBH,
        min_block_time_minutes: normalizedBH,
        block_time_minutes: normalizedBH,
    };
}

// ─── keep_all_std 时间策略 ─────────────────────────────

/**
 * STD 保持不动；STA = STD + 新轮档(分钟)。
 * 系统按轮档写出到达后，手填作废。若 STD 或轮档缺失，STA 置 null。
 */
function applyKeepAllStd(flight) {
    const newSta = computeStaFromStdAndBlockTime(flight.std, flight.block_time_minutes);
    const withoutManual = clearStaManual(flight);
    if (!newSta) {
        return { ...withoutManual, sta: null };
    }
    return { ...withoutManual, sta: newSta };
}

// ─── 警告检查 ──────────────────────────────────────────

function checkSameAirport(flight) {
    if (
        flight.departure_airport &&
        flight.arrival_airport &&
        flight.departure_airport === flight.arrival_airport
    ) {
        return WARNING_TYPES.SAME_AIRPORT;
    }
    return null;
}

/**
 * 检查过站不足：当前航班到达后，到下一班起飞前的地面时间是否小于最小过站。
 * 口径与旧版一致：过站 = 下一班 STD − 当前班 STA（同机、同场衔接）。
 *
 * 最小过站只认到达站在机场主数据里的明确配置（含 0 分钟）；航班上存着的
 * min_ground_time_minutes 不参与判断。机场没配时按“配置缺失”报出，
 * 不得拿 60 分钟当尺子去判断足不足。
 */
function checkGroundTime(currentFlight, nextFlight, airportConstraints) {
    if (!currentFlight || !nextFlight) return null;
    if (currentFlight.aircraft_reg !== nextFlight.aircraft_reg) return null;
    if (normalizeAirportCode(currentFlight.arrival_airport) !== normalizeAirportCode(nextFlight.departure_airport)) return null;

    const sta = dayjs.utc(currentFlight.sta);
    const nextStd = dayjs.utc(nextFlight.std);
    if (!sta.isValid() || !nextStd.isValid()) return null;

    const groundMinutes = nextStd.diff(sta, "minute");

    const minGround = getAirportMinGroundTime(airportConstraints, currentFlight.arrival_airport);
    if (minGround === null) {
        return WARNING_TYPES.GROUND_TIME_CONFIG_MISSING;
    }

    if (groundMinutes < minGround) {
        return WARNING_TYPES.GROUND_TIME_SHORT;
    }
    return null;
}

/**
 * 检查宵禁：复用 ConstraintsService.checkCurfewViolation。
 * 检查出发机场起飞宵禁 + 到达机场到达宵禁。
 */
function checkCurfew(flight, airportConstraints) {
    if (!airportConstraints || !flight.std || !flight.sta) return null;

    const depResult = ConstraintsService.checkCurfewViolation(
        flight.std,
        flight.departure_airport,
        "departure",
        { airportConstraints },
    );
    if (depResult?.isViolated) return WARNING_TYPES.CURFEW;

    const arrResult = ConstraintsService.checkCurfewViolation(
        flight.sta,
        flight.arrival_airport,
        "arrival",
        { airportConstraints },
    );
    if (arrResult?.isViolated) return WARNING_TYPES.CURFEW;

    return null;
}

// ─── 预览生成 ──────────────────────────────────────────

/**
 * 生成批量改机场的完整预览。
 *
 * @param {Object} params
 * @param {Array} params.flights        编辑器当前航班列表（全量）
 * @param {string} params.fromAirport   旧机场
 * @param {string} params.toAirport     新机场
 * @param {string[]} params.sides       ["departure"] / ["arrival"] / ["departure","arrival"]
 * @param {Object} params.routeMap      bulk best-match 结果 { "DEP-ARR": RouteData | null }
 * @param {Object} params.airportConstraints  机场约束（宵禁 / 过站）
 * @param {boolean} params.skipUnmatchedRoutes  勾选「跳过缺航路」
 * @returns {{ summary, legs }}
 */
function generatePreview({
    flights,
    fromAirport,
    toAirport,
    sides,
    routeMap,
    airportConstraints,
    skipUnmatchedRoutes,
}) {
    const affected = findAffectedFlights(flights, fromAirport, sides);

    if (affected.length === 0) {
        return {
            summary: buildEmptySummary(),
            legs: [],
        };
    }

    const allFlightsSorted = [...flights].sort(
        (a, b) => new Date(a.std || 0) - new Date(b.std || 0),
    );

    const legs = [];
    let matchedCount = 0;
    let skippedCount = 0;
    let groundTimeWarnings = 0;
    let curfewWarnings = 0;
    let sameAirportWarnings = 0;
    let missingRouteCount = 0;

    for (const { flight, affectedSides } of affected) {
        const before = {
            dep: flight.departure_airport,
            arr: flight.arrival_airport,
            route_id: flight.flight_route_id,
            route_name: flight.flight_route_name,
            bh: flight.block_time_minutes,
            std: flight.std,
            sta: flight.sta,
        };

        let afterFlight = replaceAirport(flight, fromAirport, toAirport, affectedSides);

        const legKey = `${afterFlight.departure_airport}-${afterFlight.arrival_airport}`;
        const routeData = routeMap?.[legKey] ?? null;
        const hasMissingRoute = !routeData;

        afterFlight = applyRouteFields(afterFlight, routeData);
        afterFlight = applyKeepAllStd(afterFlight);

        const warnings = [];

        if (hasMissingRoute) {
            warnings.push(WARNING_TYPES.MISSING_ROUTE);
            missingRouteCount++;
            if (skipUnmatchedRoutes) {
                skippedCount++;
            }
        } else {
            matchedCount++;
        }

        const sameAirportWarn = checkSameAirport(afterFlight);
        if (sameAirportWarn) {
            warnings.push(sameAirportWarn);
            sameAirportWarnings++;
        }

        const curfewWarn = checkCurfew(afterFlight, airportConstraints);
        if (curfewWarn) {
            warnings.push(curfewWarn);
            curfewWarnings++;
        }

        const after = {
            dep: afterFlight.departure_airport,
            arr: afterFlight.arrival_airport,
            route_id: afterFlight.flight_route_id,
            route_name: afterFlight.flight_route_name,
            bh: afterFlight.block_time_minutes,
            std: afterFlight.std,
            sta: afterFlight.sta,
        };

        legs.push({
            flight_id: flight.id,
            flight_number: flight.flight_number,
            flight_date: flight.flight_date,
            before,
            after,
            warnings,
            skipped: hasMissingRoute && skipUnmatchedRoutes,
            afterFlight,
        });
    }

    // 过站警告需要在改后的航班列表上下文中检查
    const afterFlightsMap = new Map();
    for (const leg of legs) {
        if (!leg.skipped) {
            afterFlightsMap.set(leg.flight_id, leg.afterFlight);
        }
    }

    const mergedFlightsSorted = allFlightsSorted.map((f) =>
        afterFlightsMap.has(f.id) ? afterFlightsMap.get(f.id) : f,
    );

    for (let i = 0; i < mergedFlightsSorted.length; i++) {
        const current = mergedFlightsSorted[i];
        const next = mergedFlightsSorted[i + 1];
        if (!next) continue;

        const leg = legs.find((l) => l.flight_id === current.id && !l.skipped);
        if (!leg) continue;

        const groundWarn = checkGroundTime(current, next, airportConstraints);
        if (groundWarn) {
            leg.warnings.push(groundWarn);
            groundTimeWarnings++;
        }
    }

    const summary = {
        matched_flight_count: matchedCount,
        skipped_flight_count: skippedCount,
        missing_route_count: missingRouteCount,
        std_preserved_count: matchedCount + (skipUnmatchedRoutes ? 0 : missingRouteCount - skippedCount),
        ground_time_warning_count: groundTimeWarnings,
        curfew_warning_count: curfewWarnings,
        same_airport_warning_count: sameAirportWarnings,
        total_affected: affected.length,
    };

    return { summary, legs };
}

function buildEmptySummary() {
    return {
        matched_flight_count: 0,
        skipped_flight_count: 0,
        missing_route_count: 0,
        std_preserved_count: 0,
        ground_time_warning_count: 0,
        curfew_warning_count: 0,
        same_airport_warning_count: 0,
        total_affected: 0,
    };
}

// ─── 确认可用性判定 ────────────────────────────────────

/**
 * 判断当前预览是否允许确认。
 * 只有「存在缺航路且未勾选跳过」时才禁用确认。
 */
function canConfirm(summary, skipUnmatchedRoutes) {
    if (summary.total_affected === 0) return false;
    if (summary.missing_route_count > 0 && !skipUnmatchedRoutes) return false;
    return true;
}

// ─── 构建写回数据 ──────────────────────────────────────

/**
 * 把预览结果写回航班列表（不落库，只改内存）。
 * 跳过的航班保持原样；其余替换为 afterFlight。
 */
function applyPreviewToFlights(flights, legs) {
    const afterMap = new Map();
    for (const leg of legs) {
        if (!leg.skipped) {
            afterMap.set(leg.flight_id, leg.afterFlight);
        }
    }

    return flights.map((f) => (afterMap.has(f.id) ? { ...afterMap.get(f.id) } : f));
}

// ─── 可追查备注 ────────────────────────────────────────

/**
 * 构建一条批量改机场的可追查备注（确认时写入本地；保存时合并进库）。
 */
function buildChangeLog({ fromAirport, toAirport, sides, skipUnmatchedRoutes, summary, operator }) {
    return {
        from_airport: fromAirport,
        to_airport: toAirport,
        sides,
        time_strategy: "keep_all_std",
        skip_unmatched_routes: skipUnmatchedRoutes,
        matched_flight_count: summary.matched_flight_count,
        skipped_flight_count: summary.skipped_flight_count,
        operator: operator || null,
        operated_at: new Date().toISOString(),
    };
}

// ─── 构建 bulk best-match 请求 ─────────────────────────

/**
 * 为受影响的航班构建去重后的航段列表，用于 bulk best-match 请求。
 */
function buildBulkLegs(flights, fromAirport, toAirport, sides) {
    const affected = findAffectedFlights(flights, fromAirport, sides);
    const seen = new Set();
    const legs = [];

    for (const { flight, affectedSides } of affected) {
        const replaced = replaceAirport(flight, fromAirport, toAirport, affectedSides);
        const key = `${replaced.departure_airport}-${replaced.arrival_airport}`;
        if (!seen.has(key)) {
            seen.add(key);
            legs.push({
                departure_airport: replaced.departure_airport,
                arrival_airport: replaced.arrival_airport,
            });
        }
    }

    return legs;
}

const BatchAirportChangeService = {
    WARNING_TYPES,
    findAffectedFlights,
    replaceAirport,
    applyRouteFields,
    applyKeepAllStd,
    checkSameAirport,
    checkGroundTime,
    checkCurfew,
    generatePreview,
    canConfirm,
    applyPreviewToFlights,
    buildChangeLog,
    buildBulkLegs,
};

export default BatchAirportChangeService;
